// src/pages/document/DocumentTypeEditPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocumentDocumentType, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';
import { FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import { ColorPicker } from '@/components/ColorPicker';
import { IconPicker } from '@/components/IconPicker';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { documentTypeColorOptions, documentConfidentialityMap, getStatusMapping } from '@/features/document/logic';

/**
 * Document type edit page.
 */
export const DocumentTypeEditPage = observer(() => {
  const { typeId } = useParams<{ typeId: string }>();
  const navigate = useNavigate();
  const store = useDocumentDocumentType();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    display_name: '',
    description: '',
    product_id: '',
    source_entity_type: '',
    icon: '',
    color: documentTypeColorOptions[0].value,
    default_workflow: '',
    default_category: '',
    default_numbering_scheme_id: '',
    default_review_period: '',
    default_confidentiality: '',
    requires_approval: false,
    requires_acknowledgment: false,
    enable_versioning: false,
    enable_review_cycle: false,
    enable_audit_log: false,
    source_created_topic: '',
    source_updated_topic: '',
    source_deleted_topic: '',
    source_status_changed_topic: '',
  });

  useEffect(() => {
    if (typeId) {
      store.GetDocumentType(typeId);
    }
    return () => {
      store.clearCurrent();
    };
  }, [typeId]);

  useEffect(() => {
    if (store.currentItem) {
      setFormData({
        slug: (store.currentItem as any).slug ?? '',
        display_name: (store.currentItem as any).display_name ?? '',
        description: (store.currentItem as any).description ?? '',
        product_id: (store.currentItem as any).product_id ?? '',
        source_entity_type: (store.currentItem as any).source_entity_type ?? '',
        icon: (store.currentItem as any).icon ?? '',
        color: (store.currentItem as any).color || documentTypeColorOptions[0].value,
        default_workflow: (store.currentItem as any).default_workflow ?? '',
        default_category: (store.currentItem as any).default_category ?? '',
        default_numbering_scheme_id: (store.currentItem as any).default_numbering_scheme_id ?? '',
        default_review_period: (store.currentItem as any).default_review_period ?? '',
        default_confidentiality: (store.currentItem as any).default_confidentiality ?? '',
        requires_approval: (store.currentItem as any).requires_approval ?? false,
        requires_acknowledgment: (store.currentItem as any).requires_acknowledgment ?? false,
        enable_versioning: (store.currentItem as any).enable_versioning ?? false,
        enable_review_cycle: (store.currentItem as any).enable_review_cycle ?? false,
        enable_audit_log: (store.currentItem as any).enable_audit_log ?? false,
        source_created_topic: (store.currentItem as any).source_created_topic ?? '',
        source_updated_topic: (store.currentItem as any).source_updated_topic ?? '',
        source_deleted_topic: (store.currentItem as any).source_deleted_topic ?? '',
        source_status_changed_topic: (store.currentItem as any).source_status_changed_topic ?? '',
      });
    }
  }, [store.currentItem]);

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!typeId) return;
    setIsSubmitting(true);

    try {
      await store.UpdateDocumentType(typeId, formData as any);
      navigate(`/documents/types/${typeId}`);
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoadingItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/documents/types" className="text-muted-foreground hover:text-foreground">
            {t('documentType.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.edit')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('documentType.edit')}</h1>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <form onSubmit={handleSubmit} noValidate>
        {/* Identity */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('documentType.identity')}</CardTitle>
            <CardDescription>{t('documentType.updateInfo')}</CardDescription>
          </CardHeader>
          <CardContent className="divide-y divide-border/60">
            <FormRow label={t('documentType.fieldSlug')} htmlFor="type-slug">
              <input
                id="type-slug"
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder={t('documentType.fieldSlugPlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('documentType.fieldDisplayName')} htmlFor="type-display-name">
              <input
                id="type-display-name"
                type="text"
                value={formData.display_name}
                onChange={(e) => handleChange('display_name', e.target.value)}
                placeholder={t('documentType.fieldDisplayNamePlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('documentType.fieldDescription')} htmlFor="type-desc">
              <input
                id="type-desc"
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('documentType.fieldDescriptionPlaceholder')}
                autoCapitalize="off"
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('documentType.fieldProductId')} htmlFor="type-product-id">
              <input
                id="type-product-id"
                type="text"
                value={formData.product_id}
                onChange={(e) => handleChange('product_id', e.target.value)}
                placeholder={t('documentType.fieldProductIdPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('documentType.fieldSourceEntityType')} htmlFor="type-source-entity">
              <input
                id="type-source-entity"
                type="text"
                value={formData.source_entity_type}
                onChange={(e) => handleChange('source_entity_type', e.target.value)}
                placeholder={t('documentType.fieldSourceEntityTypePlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('documentType.fieldIcon')}>
              <IconPicker value={formData.icon} onChange={(v) => handleChange('icon', v)} />
            </FormRow>

            <FormRow label={t('documentType.fieldColor')}>
              <ColorPicker options={documentTypeColorOptions} value={formData.color} onChange={(v) => handleChange('color', v)} />
            </FormRow>
          </CardContent>
        </Card>

        {/* Defaults */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('documentType.defaults')}</CardTitle>
            <CardDescription>{t('documentType.defaultsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="divide-y divide-border/60">
            <FormRow label={t('documentType.fieldDefaultWorkflow')} htmlFor="type-workflow">
              <input
                id="type-workflow"
                type="text"
                value={formData.default_workflow}
                onChange={(e) => handleChange('default_workflow', e.target.value)}
                placeholder={t('documentType.fieldDefaultWorkflowPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('documentType.fieldDefaultCategory')} htmlFor="type-category">
              <input
                id="type-category"
                type="text"
                value={formData.default_category}
                onChange={(e) => handleChange('default_category', e.target.value)}
                placeholder={t('documentType.fieldDefaultCategoryPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('documentType.fieldDefaultNumberingSchemeId')} htmlFor="type-scheme-id">
              <input
                id="type-scheme-id"
                type="text"
                value={formData.default_numbering_scheme_id}
                onChange={(e) => handleChange('default_numbering_scheme_id', e.target.value)}
                placeholder={t('documentType.fieldDefaultNumberingSchemeIdPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>

            <FormRow label={t('documentType.fieldDefaultReviewPeriod')} htmlFor="type-review-period" hint={t('documentType.fieldDefaultReviewPeriodHint')}>
              <span className="inline-flex items-center gap-2">
                <input
                  id="type-review-period"
                  type="number"
                  value={formData.default_review_period}
                  onChange={(e) => handleChange('default_review_period', e.target.value)}
                  placeholder="—"
                  className="w-24 rounded-md border border-input bg-background px-3 py-1.5 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {formData.default_review_period && (
                  <button
                    type="button"
                    onClick={() => handleChange('default_review_period', '')}
                    aria-label={t('common.clear')}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </button>
                )}
              </span>
            </FormRow>

            <FormRow label={t('documentType.fieldDefaultConfidentiality')} htmlFor="type-confidentiality">
              <Select
                id="type-confidentiality"
                value={formData.default_confidentiality}
                onChange={(e) => handleChange('default_confidentiality', e.target.value)}
                fullWidth={false}
                className="w-64"
                placeholder={t('documentType.fieldDefaultConfidentialityPlaceholder')}
              >
                {Object.keys(documentConfidentialityMap).map((conf) => {
                  const info = getStatusMapping(documentConfidentialityMap, conf);
                  return (
                    <option key={conf} value={conf}>
                      {t(info.labelKey)}
                    </option>
                  );
                })}
              </Select>
            </FormRow>
          </CardContent>
        </Card>

        {/* Lifecycle Configuration */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('documentType.lifecycle')}</CardTitle>
            <CardDescription>{t('documentType.lifecycleDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1 divide-y divide-border/60">
            <div className="py-2">
              <ToggleSwitch
                label={t('documentType.requiresApproval')}
                checked={formData.requires_approval}
                onChange={(v) => handleChange('requires_approval', v)}
              />
            </div>
            <div className="py-2">
              <ToggleSwitch
                label={t('documentType.requiresAcknowledgment')}
                checked={formData.requires_acknowledgment}
                onChange={(v) => handleChange('requires_acknowledgment', v)}
              />
            </div>
            <div className="py-2">
              <ToggleSwitch
                label={t('documentType.enableVersioning')}
                checked={formData.enable_versioning}
                onChange={(v) => handleChange('enable_versioning', v)}
              />
            </div>
            <div className="py-2">
              <ToggleSwitch
                label={t('documentType.enableReviewCycle')}
                checked={formData.enable_review_cycle}
                onChange={(v) => handleChange('enable_review_cycle', v)}
              />
            </div>
            <div className="py-2">
              <ToggleSwitch
                label={t('documentType.enableAuditLog')}
                checked={formData.enable_audit_log}
                onChange={(v) => handleChange('enable_audit_log', v)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Kafka Topics */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('documentType.kafkaTopics')}</CardTitle>
            <CardDescription>{t('documentType.kafkaTopicsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="divide-y divide-border/60">
            <FormRow label={t('documentType.fieldSourceCreatedTopic')} htmlFor="type-topic-created">
              <input
                id="type-topic-created"
                type="text"
                value={formData.source_created_topic}
                onChange={(e) => handleChange('source_created_topic', e.target.value)}
                placeholder={t('documentType.fieldSourceCreatedTopicPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
            <FormRow label={t('documentType.fieldSourceUpdatedTopic')} htmlFor="type-topic-updated">
              <input
                id="type-topic-updated"
                type="text"
                value={formData.source_updated_topic}
                onChange={(e) => handleChange('source_updated_topic', e.target.value)}
                placeholder={t('documentType.fieldSourceUpdatedTopicPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
            <FormRow label={t('documentType.fieldSourceDeletedTopic')} htmlFor="type-topic-deleted">
              <input
                id="type-topic-deleted"
                type="text"
                value={formData.source_deleted_topic}
                onChange={(e) => handleChange('source_deleted_topic', e.target.value)}
                placeholder={t('documentType.fieldSourceDeletedTopicPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
            <FormRow label={t('documentType.fieldSourceStatusChangedTopic')} htmlFor="type-topic-status">
              <input
                id="type-topic-status"
                type="text"
                value={formData.source_status_changed_topic}
                onChange={(e) => handleChange('source_status_changed_topic', e.target.value)}
                placeholder={t('documentType.fieldSourceStatusChangedTopicPlaceholder')}
                className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to={`/documents/types/${typeId}`}>
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button type="submit" disabled={isSubmitting || !formData.slug.trim() || !formData.display_name.trim()}>
              {isSubmitting ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default DocumentTypeEditPage;
